import { type EditorModel } from '@/lib/types';
import { useState } from 'react';
import Dialog from '@/components/Dialog';
import { IconButton, Tooltip } from '@mui/material';
import { SettingsOutlined as SettingsIcon } from '@mui/icons-material';
import styled from '@emotion/styled';
import ModelLabelEditor from './ModelNameEditor';
import ModelActivationControls from './ModelActivationControls';
import ModelDefaultControls from './ModelDefaultControls';
import { FormattedMessage, useIntl } from 'react-intl';
import FormattedHtmlMessage from '@/components/FormattedHtmlMessage';

const SettingsDialog = styled(Dialog)(({ theme }) => ({
  '.MuiDialogTitle-root': {
    display: 'flex',
    alignItems: 'center',

    '.MuiSvgIcon-root': {
      marginRight: 8,
      color: theme.palette.text.secondary,
    },
  },
  h3: {
    marginTop: 24,
    marginBottom: 6,
    fontFamily: 'inherit',
    fontSize: '1em',
    fontWeight: 700,
  },
}));

type ModelSettingsProps = {
  model: EditorModel;
};

const ModelSettings = ({ model }: ModelSettingsProps) => {
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);

  const intl = useIntl();
  const buttonTooltip = intl.formatMessage({
    id: 'editor.model_editor.model_selector.model_settings.button_tooltip',
  });

  return (
    <>
      <Tooltip title={buttonTooltip}>
        <IconButton size="small" onClick={() => setSettingsDialogOpen(true)}>
          <SettingsIcon />
        </IconButton>
      </Tooltip>
      <SettingsDialog
        open={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
      >
        <Dialog.Title>
          <SettingsIcon />
          <FormattedMessage id="editor.model_editor.model_selector.model_settings.dialog.title" />
        </Dialog.Title>
        <Dialog.Content>
          <Dialog.ContentText>
            <FormattedHtmlMessage
              id={`editor.model_editor.model_selector.model_settings.dialog.text${model.name === null ? '.no_model_name' : ''}`}
              values={{
                modelName: model.name,
              }}
            />
          </Dialog.ContentText>
          <ModelLabelEditor model={model} />
          <ModelActivationControls model={model} />
          {!model.isDefault && <ModelDefaultControls model={model} />}
        </Dialog.Content>
        <Dialog.Actions>
          <Dialog.Actions.DoneButton
            onClick={() => setSettingsDialogOpen(false)}
          />
        </Dialog.Actions>
      </SettingsDialog>
    </>
  );
};

export default ModelSettings;
